// src/pages/MyCoursesPage.jsx
"use client";

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetCoursesQuery,
  useGetCourseProgressQuery,
} from "@/features/api/courseApi";
import { useGetOrdersQuery } from "@/features/api/orderApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { BookOpen, Video } from "lucide-react";

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  /* ------------------------------------------
     Redirect if not logged in
  ------------------------------------------ */
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  /* ------------------------------------------
     Fetch SUCCESS Orders (Paid + Free)
  ------------------------------------------ */
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isError: ordersError,
    refetch: refetchOrders,
  } = useGetOrdersQuery(
    { status: "SUCCESS", limit: 100 },
    { skip: !user }
  );

  /* ------------------------------------------
     Fetch All Courses
  ------------------------------------------ */
  const {
    data: coursesData,
    isLoading: coursesLoading,
  } = useGetCoursesQuery(undefined, { skip: !user });

  const allCourses = coursesData?.courses || [];

  /* ------------------------------------------
     Extract Purchased Course IDs
  ------------------------------------------ */
  const purchasedCourseIds = useMemo(() => {
    if (!ordersData?.data?.length) return [];

    return [
      ...new Set(
        ordersData.data
          .filter((order) => order.status === "SUCCESS")
          .map((order) => order.courseId)
      ),
    ];
  }, [ordersData]);

  /* ------------------------------------------
     Filter Purchased Courses
  ------------------------------------------ */
  const purchasedCourses = useMemo(() => {
    if (!purchasedCourseIds.length || !allCourses.length) return [];

    return allCourses.filter((course) =>
      purchasedCourseIds.includes(course._id)
    );
  }, [purchasedCourseIds, allCourses]);

  /* ------------------------------------------
     Error Handling
  ------------------------------------------ */
  useEffect(() => {
    if (ordersError) {
      toast.error("Failed to load your purchased courses.");
    }
  }, [ordersError]);

  /* ------------------------------------------
     Skeleton Loader
  ------------------------------------------ */
  const CourseSkeleton = () => (
    <div className="w-full animate-pulse space-y-3">
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-4 w-3/4 rounded" />
      <Skeleton className="h-4 w-1/2 rounded" />
      <Skeleton className="h-10 w-full rounded" />
    </div>
  );

  /* ------------------------------------------
     UI
  ------------------------------------------ */
  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-center mb-10">
          🎓 My Courses
        </h1>

        {ordersLoading || coursesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <CourseSkeleton key={i} />
            ))}
          </div>
        ) : ordersError ? (
          <div className="text-center text-red-600 text-lg font-medium">
            Failed to load your courses.
            <Button onClick={refetchOrders} className="ml-2">
              Retry
            </Button>
          </div>
        ) : purchasedCourses.length === 0 ? (
          <div className="text-center text-gray-500 text-lg font-medium">
            📭 You haven’t enrolled in any courses yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {purchasedCourses.map((course) => (
              <CourseCard
                key={course._id}
                course={course}
                navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* =====================================================
   Separate CourseCard Component (Correct Progress Fetch)
===================================================== */

const CourseCard = ({ course, navigate }) => {
  const {
    data: progressData,
    isLoading: progressLoading,
  } = useGetCourseProgressQuery(course._id);

  const percentage = progressData?.percentage || 0;

  let buttonText = "Start Now";
  if (progressLoading) buttonText = "Loading...";
  else if (percentage === 100) buttonText = "Completed";
  else if (percentage > 0) buttonText = "Continue";

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
      {course.featuredImage ? (
        <img
          src={
            typeof course.featuredImage === "string"
              ? course.featuredImage
              : course.featuredImage?.url || ""
          }
          alt={course.altText || course.title}
          className="w-full h-40 object-cover"
        />
      ) : (
        <div className="w-full h-40 bg-muted flex items-center justify-center">
          <Video className="h-10 w-10 text-muted-foreground" />
        </div>
      )}

      <CardHeader className="p-4">
        <h2 className="font-semibold line-clamp-2 text-lg">
          {course.title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {course.instructorName || "Instructor"}
        </p>
      </CardHeader>

      <CardContent className="p-4 flex flex-col gap-3 flex-grow">
        {progressLoading ? (
          <Skeleton className="h-4 w-1/2" />
        ) : (
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{percentage}%</span>
            </div>
            <Progress value={percentage} />
          </div>
        )}

        <Button
          className="w-full mt-auto"
          onClick={() =>
            navigate(`/courses/${course.slug}/learn`)
          }
          disabled={progressLoading}
        >
          <BookOpen className="mr-2 h-4 w-4" />
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );
};

export default MyCoursesPage;